import { ClipboardList, FileText, MapPinned, Receipt, ShieldCheck, Smartphone } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Section } from "@/components/common/Section";
import { useAuth } from "@/context/AuthContext";

const FLOW = [
  { icon: ClipboardList, title: "Ordre", text: "Administrasjonen opprettar ordre med kunde, stader, dato og tildelte medarbeidarar." },
  { icon: MapPinned, title: "Kalkyle og rute", text: "Adressesøk, køyrerute og prisoverslag (materiale, transport, ferje og tillegg) blir lagra på ordra." },
  { icon: Smartphone, title: "Utføring", text: "Medarbeidarar fører timar, køyretøy, varer, merknader og vedlegg direkte frå mobilen." },
  { icon: Receipt, title: "Fakturagrunnlag", text: "Utførte ordrar blir kontrollerte, prissette og godkjende som låst revisjon før eksport." },
];

export default function OmSystemetPage() {
  const { isAdmin } = useAuth();
  return (
    <div className="space-y-5" data-testid="om-systemet-page">
      <PageHeader
        title="Om systemet"
        description="Internt ordresystem for Ekeland Transport AS. Frå førespurnad og kalkyle til utføring og fakturagrunnlag – i eitt verktøy."
      />

      <Section title="Slik heng flyten saman" testId="om-systemet-flyt">
        <ol className="grid gap-3 md:grid-cols-2">
          {FLOW.map((step, i) => (
            <li key={step.title} className="flex gap-3 rounded-lg border border-border bg-background p-3">
              <span className="num flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-sm font-semibold">{i + 1}</span>
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-semibold"><step.icon className="h-4 w-4 text-primary" /> {step.title}</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Roller og tilgang" testId="om-systemet-roller">
        <ul className="space-y-2 text-sm leading-6">
          <li className="flex gap-2"><ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-primary" /><span><strong>Administrasjon</strong> ser alle ordrar, prisar, kalkylar og fakturagrunnlag, og styrer register for kundar, katalog, køyretøy og brukarar.</span></li>
          <li className="flex gap-2"><ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-primary" /><span><strong>Medarbeidar</strong> ser berre ordrar dei er tildelte, og aldri prisar eller fakturadata – heller ikkje i mobilvisning eller via API.</span></li>
        </ul>
      </Section>

      <Section title="Gode vanar" testId="om-systemet-vanar">
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6">
          <li>Før timar med desimal-komma: <span className="num">1,5</span> t blir 90 minutt. Du ser minuttane før du lagrar.</li>
          <li>Lagre éin gong – systemet hindrar dobbeltregistrering ved nytt forsøk eller dårleg dekning.</li>
          <li>Last opp vegesetel, følgjeseddel og bilete som vedlegg på ordra, så er dokumentasjonen klar til fakturering.</li>
          <li>Meld utført når jobben er ferdig. Då går ordra til kontroll hos administrasjonen.</li>
        </ul>
      </Section>

      {isAdmin ? (
        <Section title="Kalkyle, kart og eksport" testId="om-systemet-admin">
          <ul className="list-disc space-y-1 pl-5 text-sm leading-6">
            <li>Adressesøk og køyrerute kjem frå Google. Ruta er rekna for personbil – tungbiltilkomst (vekt, høgd, lengd) må alltid kontrollerast mot veglistene.</li>
            <li>Kalkylen er eit overslag. Føresetnadene (densitet, tal lass, ferjetakst) blir lagra saman med kalkylen og endrar seg ikkje om prisane i katalogen seinare blir justerte.</li>
            <li>Godkjent fakturagrunnlag er ein låst revisjon. Korrigering lagar ny revisjon, og alle tidlegare revisjonar, eksportar og vedlegg blir tekne vare på.</li>
            <li className="flex items-start gap-2"><FileText className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" /><span>Eksport: PDF (internt fakturagrunnlag) og CSV (UTF-8, semikolon, desimal-komma). Same revisjon gir identisk fil kvar gong.</span></li>
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
